interface MessageModalElementDimensions {
  scrollWidth: number | null;
  scrollHeight: number | null;
  clientWidth: number | null;
  clientHeight: number | null;
  offsetWidth: number | null;
  offsetHeight: number | null;
}

interface MessageModalVisualViewportDimensions {
  width: number | null;
  height: number | null;
  offsetTop: number | null;
  scale: number | null;
}

interface MessageModalScrollDimensionsSnapshotInput {
  root: HTMLDivElement | null;
  body: HTMLElement;
  html: HTMLElement;
  visualViewport: VisualViewport | null;
  bodyZoom: string | undefined;
  windowInnerWidth: number;
  windowInnerHeight: number;
  windowScrollX: number;
  windowScrollY: number;
  vw: number;
  vh: number;
}

function toNullableNumber(value: number | undefined): number | null {
  return value ?? null;
}

function toNullableString(value: string | undefined): string | null {
  return value ?? null;
}

function getElementDimensions(
  element: HTMLElement | null,
): MessageModalElementDimensions {
  if (!element) {
    return {
      scrollWidth: null,
      scrollHeight: null,
      clientWidth: null,
      clientHeight: null,
      offsetWidth: null,
      offsetHeight: null,
    };
  }

  return {
    scrollWidth: element.scrollWidth,
    scrollHeight: element.scrollHeight,
    clientWidth: element.clientWidth,
    clientHeight: element.clientHeight,
    offsetWidth: element.offsetWidth,
    offsetHeight: element.offsetHeight,
  };
}

function getVisualViewportDimensions(
  visualViewport: VisualViewport | null,
): MessageModalVisualViewportDimensions {
  if (!visualViewport) {
    return {
      width: null,
      height: null,
      offsetTop: null,
      scale: null,
    };
  }

  return {
    width: toNullableNumber(visualViewport.width),
    height: toNullableNumber(visualViewport.height),
    offsetTop: toNullableNumber(visualViewport.offsetTop),
    scale: toNullableNumber(visualViewport.scale),
  };
}

export function buildMessageModalScrollDimensionsSnapshot({
  root,
  body,
  html,
  visualViewport,
  bodyZoom,
  windowInnerWidth,
  windowInnerHeight,
  windowScrollX,
  windowScrollY,
  vw,
  vh,
}: MessageModalScrollDimensionsSnapshotInput): Record<string, number | string | null> {
  const visualViewportDimensions = getVisualViewportDimensions(visualViewport);
  const bodyDimensions = getElementDimensions(body);
  const htmlDimensions = getElementDimensions(html);
  const rootDimensions = getElementDimensions(root);

  return {
    windowInnerWidth,
    windowInnerHeight,
    visualViewportWidth: visualViewportDimensions.width,
    visualViewportHeight: visualViewportDimensions.height,
    visualViewportOffsetTop: visualViewportDimensions.offsetTop,
    visualViewportScale: visualViewportDimensions.scale,
    bodyScrollWidth: bodyDimensions.scrollWidth,
    bodyScrollHeight: bodyDimensions.scrollHeight,
    bodyClientWidth: bodyDimensions.clientWidth,
    bodyClientHeight: bodyDimensions.clientHeight,
    bodyOffsetWidth: bodyDimensions.offsetWidth,
    bodyOffsetHeight: bodyDimensions.offsetHeight,
    htmlScrollWidth: htmlDimensions.scrollWidth,
    htmlScrollHeight: htmlDimensions.scrollHeight,
    htmlClientWidth: htmlDimensions.clientWidth,
    htmlClientHeight: htmlDimensions.clientHeight,
    bodyZoom: toNullableString(bodyZoom),
    modalVwTimes100: vw * 100,
    modalVhTimes100: vh * 100,
    rootScrollWidth: rootDimensions.scrollWidth,
    rootScrollHeight: rootDimensions.scrollHeight,
    rootClientWidth: rootDimensions.clientWidth,
    rootClientHeight: rootDimensions.clientHeight,
    rootOffsetWidth: rootDimensions.offsetWidth,
    rootOffsetHeight: rootDimensions.offsetHeight,
    windowScrollX,
    windowScrollY,
    bodyScrollTop: body.scrollTop,
    htmlScrollTop: html.scrollTop,
  };
}
